import { DatePicker, Form, Input, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    getInstructors,
    selectAssignableInstructors,
} from "store/slices/instructorsSlice";
import { getInternships } from "store/slices/internshipsSlice";
import { getTrainings } from "store/slices/trainingsSlice";
const AbsenceTypes = ["Formation", "Stage"];

export default function AbsenceForm({ onSubmit, form }) {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state?.absences);
    const { trainings } = useSelector((state) => state?.trainings);
    const { internships } = useSelector((state) => state?.internships);
    const instructors = useSelector(selectAssignableInstructors);
    const [selectedType, setSelectedType] = useState(
        form?.getFieldValue("absenceManagementType") || "Formation"
    );
    const [selectedTraining, setSelectedTraining] = useState(
        form?.getFieldValue("formationId") || null
    );
    const [selectedInternship, setSelectedInternship] = useState(
        form?.getFieldValue("stageId") || null
    );
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedInternship(null);
        setSelectedTraining(null);
        switch (selectedType) {
            case "Formation":
                dispatch(getTrainings(1));
                dispatch(getInstructors());
                break;
            case "Stage":
                dispatch(getInternships());
                break;
            default:
                break;
        }
    }, [dispatch, selectedType]);

    return (
        <Form
            layout="vertical"
            name="absence"
            id="absence_form"
            form={form}
            onFinish={onSubmit}
            disabled={loading}
            style={{
                maxWidth: 600,
                margin: "0 auto",
            }}
        >
            <Form.Item
                label={t("absences.crud.form.label.type")}
                name="absenceManagementType"
                rules={[
                    {
                        required: true,
                        message: t(
                            "absences.crud.form.error.label.required.type"
                        ),
                    },
                ]}
            >
                <Select onChange={setSelectedType} value={selectedType}>
                    {AbsenceTypes?.map((type) => (
                        <Select.Option key={type} value={type}>
                            {t(`absences.type.options.${type?.toLowerCase()}`)}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            {selectedType === "Formation" && (
                <>
                    <Form.Item
                        label={t("absences.crud.form.label.training")}
                        name="formationId"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    "absences.crud.form.error.label.required.training"
                                ),
                            },
                        ]}
                    >
                        <Select
                            onChange={setSelectedTraining}
                            value={selectedTraining}
                        >
                            {trainings?.map((training) => (
                                <Select.Option
                                    key={training?._id}
                                    value={training?._id}
                                >
                                    {training?.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t("absences.crud.form.label.instructor")}
                        name="formateurId"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    "absences.crud.form.error.label.required.instructor"
                                ),
                            },
                        ]}
                    >
                        <Select>
                            {instructors?.map((instructor) => (
                                <Select.Option
                                    key={instructor?._id}
                                    value={instructor?._id}
                                >
                                    {instructor?.fullName} ({instructor?.email})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={t("absences.crud.form.label.student")}
                        name="studentFormationId"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    "absences.crud.form.error.label.required.student"
                                ),
                            },
                        ]}
                    >
                        <Select>
                            {trainings?.find((t) => t?._id === selectedTraining)
                                ?.students?.map((student) => (
                                    <Select.Option
                                        key={student?.studentFormationId}
                                        value={student?.studentFormationId}
                                    >
                                        {student?.studentName} (
                                        {student?.studentEmail})
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                </>
            )}
            {selectedType === "Stage" && (
                <>
                    <Form.Item
                        label={t("absences.crud.form.label.internship")}
                        name="stageId"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    "absences.crud.form.error.label.required.internship"
                                ),
                            },
                        ]}
                    >
                        <Select
                            onChange={setSelectedInternship}
                            value={selectedInternship}
                        >
                            {internships?.map((internship) => (
                                <Select.Option
                                    key={internship?._id}
                                    value={internship?._id}
                                >
                                    {internship?.titled} ({internship?.schoolName}
                                    )
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={t("absences.crud.form.label.student")}
                        name="studentStageId"
                        rules={[
                            {
                                required: true,
                                message: t(
                                    "absences.crud.form.error.label.required.student"
                                ),
                            },
                        ]}
                    >
                        <Select>
                            {internships?.find((i) => i?._id === selectedInternship)
                                ?.students?.map((student) => (
                                    <Select.Option
                                        key={student?.studentStageId}
                                        value={student?.studentStageId}
                                    >
                                        {student?.studentName} (
                                        {student?.studentEmail})
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                </>
            )}
            <Form.Item
                label={t("absences.crud.form.label.date")}
                name="date"
                rules={[
                    {
                        required: true,
                        message: t(
                            "absences.crud.form.error.label.required.date"
                        ),
                    },
                ]}
            >
                <DatePicker
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    disabledDate={(current) =>
                        current && current > dayjs()?.endOf("day")
                    }
                />
            </Form.Item>
            <Form.Item
                label={t("absences.crud.form.label.time")}
                name="hour"
                rules={[
                    {
                        required: true,
                        message: t(
                            "absences.crud.form.error.label.required.time"
                        ),
                    },
                ]}
            >
                <TimePicker style={{ width: "100%" }} format="HH:mm" />
            </Form.Item>
            <Form.Item
                label={t("absences.crud.form.label.reason")}
                name="motif"
                rules={[
                    {
                        required: true,
                        message: t(
                            "absences.crud.form.error.label.required.reason"
                        ),
                    },
                ]}
            >
                <Input.TextArea />
            </Form.Item>
        </Form>
    );
}
